// src/pages/organization/members/CreateRoleForMember.js
import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link, useLocation } from "react-router-dom";
import { useMainPageTitle } from "../../../contexts/PageContext";
import ProfilePicture from "../../../components/profileImage";
const API_URL = "https://urchin-app-vijhz-lvk4i.ondigitalocean.app/api/v1";

const CreateRoleForMember = () => {
  const { memberId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Determine base path based on current route
  const basePath = location.pathname.startsWith('/supervisor') ? '/supervisor/members' : '/organization/members';
  
  const [supervisor, setSupervisor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [roleName, setRoleName] = useState("");

  const { updatePageTitle } = useMainPageTitle();
  useEffect(() => {
    updatePageTitle("Create Role", "Create a new role for a member");
  }, [updatePageTitle]);

  // Role permissions state with expanded categories
  const [permissions, setPermissions] = useState({
    supervisor: {
      create: false,
      update: false,
      read: false,
      delete: false,
    },
    supervisorRole: {
      update: false,
    },
    teams: {
      create: false,
      read: false,
      update: false,
      delete: false,
    },
    teamSupervisorAssign: {
      create: false,
      delete: false,
    },
    teamSupervisorAssignRole: {
      update: false,
    },

    events: {
      create: false,
      read: false,
      update: false,
      delete: false,
    },
    users: {
      create: false,
      read: false,
      update: false,
      delete: false,
    },
    reports: {
      create: false,
      read: false,
      export: false,
    },
  });

  // Fetch supervisor data
  useEffect(() => {
    const fetchSupervisor = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("token");
        const response = await fetch(
          `${API_URL}/supervisor/get-supervisor-by-id/?id=${memberId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch supervisor data");
        }

        const data = await response.json();
        console.log(data.data.supervisor.metaData.roles);
        setSupervisor(data.data.supervisor);

        // If supervisor already has permissions, set them
        if (data.data.supervisor.metaData.permissions) {
          setPermissions((prev) => ({
            ...prev,
            ...data.data.supervisor.metaData.permissions,
          }));
        }

        setLoading(false);
      } catch (err) {
        setError("Failed to fetch supervisor data");
        setLoading(false);
        console.error(err);
      }
    };

    fetchSupervisor();
  }, [memberId]);

  // Handle permission changes
  const handlePermissionChange = (category, action) => (event) => {
    setPermissions({
      ...permissions,
      [category]: {
        ...permissions[category],
        [action]: event.target.checked,
      },
    });
  };

  // Handle select all for a category
  const handleSelectAll = (category) => () => {
    const allSelected = Object.values(permissions[category]).every(
      (value) => value === true
    );

    const updatedCategoryPermissions = {};
    Object.keys(permissions[category]).forEach((action) => {
      updatedCategoryPermissions[action] = !allSelected;
    });

    setPermissions({
      ...permissions,
      [category]: updatedCategoryPermissions,
    });
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // setLoading(true);
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${API_URL}/supervisor/update-permissions/?supervisorId=${memberId}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            role: permissions,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to update permissions");
      }

      setSuccess(true);
      // scroll to top
      navigate(`${basePath}/${memberId}`);
    } catch (err) {
      setError("Failed to update permissions");
      console.error(err);
      setLoading(false);
    }
  };

  // Render permission section
  const renderPermissionSection = (category, title, icon) => {
    const actions = Object.keys(permissions[category]);
    const allSelected = actions.every(
      (action) => permissions[category][action]
    );

    return (
      <div className="mb-8 bg-white rounded-lg shadow-sm p-6 border border-gray-100 hover:shadow-md transition-shadow">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-3">
            {icon}
            <h3 className="text-lg font-semibold text-gray-700">{title}</h3>
          </div>
          <button
            type="button"
            onClick={handleSelectAll(category)}
            className="text-sm bg-gray-50 hover:bg-gray-100 text-gray-700 px-3 py-1 rounded-md transition-colors"
          >
            {allSelected ? "Deselect All" : "Select All"}
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
          {actions.map((action) => (
            <div
              key={`${category}-${action}`}
              className="flex items-center p-2 rounded-md hover:bg-gray-50"
            >
              <input
                type="checkbox"
                id={`${category}-${action}`}
                className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                checked={permissions[category][action]}
                onChange={handlePermissionChange(category, action)}
              />
              <label
                htmlFor={`${category}-${action}`}
                className="ml-3 text-gray-700 capitalize cursor-pointer select-none"
              >
                {action}
              </label>
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (loading && !supervisor) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {success && (
        <div className="bg-green-50 border-l-4 border-green-500 text-green-700 p-4 rounded shadow-md mb-6 animate-fadeIn">
          <div className="flex items-center">
            <svg
              className="h-6 w-6 text-green-500 mr-3"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M5 13l4 4L19 7"
              />
            </svg>
            <p className="font-medium">
              Permissions updated successfully! Redirecting...
            </p>
          </div>
        </div>
      )}

      {supervisor && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6 border border-gray-100">
          <div className="flex flex-col md:flex-row items-start gap-6">
            <div className="flex-shrink-0">
              <ProfilePicture
                image={supervisor.profilePicture}
                alt="Profile"
                className="w-24 h-24 rounded-full overflow-hidden ring-4 ring-gray-100 shadow-md"
                user={{
                  firstName: supervisor.firstName,
                  lastName: supervisor.lastName,
                  userName: supervisor.userName,
                  accountType: supervisor.accountType,
                  profilePicture: supervisor.profilePicture
                }}
              />
            </div>
            <div className="flex-grow">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4">
                <div>
                  <h2 className="text-2xl font-bold text-gray-800">
                    {supervisor.firstName} {supervisor.lastName}
                  </h2>
                  <p className="text-gray-500">@{supervisor.userName}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-sm text-gray-500 mb-1">Email</p>
                  <p className="text-gray-800 font-medium">
                    {supervisor.email}
                  </p>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-sm text-gray-500 mb-1">Phone</p>
                  <p className="text-gray-800 font-medium">
                    {supervisor.phone || "Not provided"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="">
        <div className="">
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-2">
              Permission Settings
            </h2>
            <p className="text-gray-500">
              Select the permissions you want to grant to this supervisor. These
              permissions will determine what actions they can perform in the
              system.
            </p>
          </div>
          {error && (
            <div className="my-4">
              <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded shadow-md">
                <div className="flex items-center">
                  <svg
                    className="h-6 w-6 text-red-500 mr-3"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                  <p className="font-medium">{error}</p>
                </div>
              </div>
            </div>
          )}
          {renderPermissionSection(
            "supervisor",
            "Supervisor Management",
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 text-purple-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2"
              />
            </svg>
          )}

          {renderPermissionSection(
            "supervisorRole",
            "Supervisor Role Management",
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 text-purple-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
              />
            </svg>
          )}

          {renderPermissionSection(
            "teams",
            "Teams Management",
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 text-blue-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
              />
            </svg>
          )}

          {renderPermissionSection(
            "teamSupervisorAssign",
            "Team Supervisor Assign",
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 text-blue-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
              />
            </svg>
          )}

          {renderPermissionSection(
            "teamSupervisorAssignRole",
            "Team Supervisor Assign Role",
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 text-blue-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
              />
            </svg>
          )}

          {renderPermissionSection(
            "events",
            "Events Management",
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 text-green-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
          )}

          {renderPermissionSection(
            "users",
            "Users Management",
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 text-orange-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
              />
            </svg>
          )}

          {renderPermissionSection(
            "reports",
            "Reports Management",
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 text-red-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          )}
        </div>

        <div className="flex justify-end gap-4 rounded-b-lg">
          <Link
            to={basePath}
            className="px-6 py-2 rounded-md text-gray-700 border border-gray-300 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </Link>
          <button
            type="submit"
            className={`px-6 py-2 rounded-md text-white font-medium ${
              loading
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
            }`}
            disabled={loading}
          >
            {loading ? (
              <span className="flex items-center">
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Saving...
              </span>
            ) : (
              "Save Permissions"
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateRoleForMember;
